import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Text,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CHART_CHROME } from "@/shared/ui/chart/public";
import { MAGNITUDE_FILL, MARK, UNKNOWN_FILL } from "./chartTheme";
import type { CountBarChartProps } from "./contract";

// 막대 하나가 차지하는 높이. 값 수에 비례해 높이를 정한다. 그래서 항목이 적은 축은 빈 캔버스를
// 남기지 않고, 항목이 많은 축은 막대가 서로 붙지 않는다.
const ROW_HEIGHT = 28;
const CHROME_HEIGHT = 24;

// 기본 틱은 차트 축 폭에 맞춰 공백이 있는 라벨을 줄바꿈한다("Sigma Gripper Tip" 이 두 줄이 되어
// 이웃 행과 겹친다). 공백 없는 긴 라벨은 왼쪽으로 넘친다. 그래서 카테고리 라벨은 항상 **한 줄
// 말줄임**으로 그린다. 잘린 전체값은 네이티브 <title> 이 hover 로 보여 준다(CountHeatmap 의 title 관례).
function CategoryTick({
  x,
  y,
  width,
  payload,
}: {
  x?: number;
  y?: number;
  width?: number;
  payload?: { value?: string | number };
}) {
  const label = String(payload?.value ?? "");
  return (
    <g>
      <title>{label}</title>
      <Text
        x={x}
        y={y}
        width={width}
        maxLines={1}
        textAnchor="end"
        verticalAnchor="middle"
        fill={CHART_CHROME.axis}
        fontSize={MARK.tickFontSize}
      >
        {label}
      </Text>
    </g>
  );
}

/**
 * 값별 개수를 가로 막대로 비교한다.
 *
 * 색은 단일 hue 다. 막대 길이가 이미 크기를 표시하므로 값마다 색을 바꾸지 않는다. 미판정
 * 묶음만 중립색으로 그린다. 호출부가 정렬과 접기를 끝낸 데이터를 전달한다.
 */
export function CountBarChart({ data, formatCount, labelWidth = 120 }: CountBarChartProps) {
  const rows = data.map((datum) => ({
    key: datum.key,
    label: datum.label,
    count: datum.count,
    fill: datum.unknown === true ? UNKNOWN_FILL : MAGNITUDE_FILL,
  }));
  return (
    <ResponsiveContainer width="100%" height={rows.length * ROW_HEIGHT + CHROME_HEIGHT}>
      <BarChart data={rows} layout="vertical" margin={{ top: 0, right: 44, bottom: 0, left: 0 }}>
        <CartesianGrid stroke={CHART_CHROME.grid} horizontal={false} />
        <XAxis type="number" hide />
        <YAxis
          type="category"
          dataKey="label"
          width={labelWidth}
          tick={<CategoryTick />}
          tickLine={false}
          axisLine={false}
        />
        <Tooltip
          cursor={false}
          formatter={(value: number) => [formatCount(value), "Dataset"]}
          contentStyle={{
            background: CHART_CHROME.tooltipBg,
            border: `1px solid ${CHART_CHROME.tooltipBorder}`,
            borderRadius: MARK.radius,
            color: CHART_CHROME.tooltipInk,
            fontSize: MARK.tickFontSize,
          }}
          itemStyle={{ color: CHART_CHROME.tooltipInk }}
        />
        <Bar dataKey="count" radius={[0, MARK.radius, MARK.radius, 0]} isAnimationActive={false}>
          {rows.map((row) => (
            <Cell key={row.key} fill={row.fill} />
          ))}
          {/* 막대가 적고 값이 핵심이라 막대 끝에 직접 라벨을 단다(차트 눈금 대신). */}
          <LabelList
            dataKey="count"
            position="right"
            fontSize={MARK.tickFontSize}
            fill={CHART_CHROME.axis}
            formatter={(value: number) => formatCount(value)}
          />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
